/**
 * New Chat Configuration Modal Component
 * Allows users to select character and model before creating a new chat
 */

import React, { useState, useEffect } from 'react';
import {
  Modal,
  Stack,
  Card,
  Group,
  Text,
  Button,
  Box,
  Avatar,
  Divider,
} from '@mantine/core';
import { IconMessagePlus, IconUser, IconBrain, IconChevronRight, IconCash } from '@tabler/icons-react';
import type { ModelInfo, CharacterInfo } from '@gofranz/gpt-muse-common';
import { getCharacterImagePath, getCharacterDisplayText } from '../../lib/character-utils';
import { formatPricing } from '../../lib/model-utils';
import { getModelBadges } from '../../lib/model-ui-utils';
import { getDefaultModel } from '../../lib/chat-utils';
import { CharacterSelectionModal } from './CharacterSelectionModal';
import { ModelSelectionModal } from './ModelSelectionModal';

interface NewChatModalProps {
  opened: boolean;
  onClose: () => void;
  selectedModel: string;
  selectedCharacter: string | null;
  models: ModelInfo[];
  characters: CharacterInfo[];
  isLoggedIn: boolean;
  onConfirm: (modelId: string, characterId: string | null) => void;
}

export const NewChatModal = React.memo<NewChatModalProps>(({
  opened,
  onClose,
  selectedModel,
  selectedCharacter,
  models,
  characters,
  isLoggedIn,
  onConfirm,
}) => {
  const [tempModel, setTempModel] = useState(selectedModel || getDefaultModel(models));
  const [tempCharacter, setTempCharacter] = useState<string | null>(selectedCharacter);
  const [characterModalOpen, setCharacterModalOpen] = useState(false);
  const [modelModalOpen, setModelModalOpen] = useState(false);

  // Sync temporary state with props when modal opens or props change
  useEffect(() => {
    if (opened) {
      // Use selectedModel if available, otherwise get default model
      const modelToUse = selectedModel || getDefaultModel(models);
      setTempModel(modelToUse);
      setTempCharacter(selectedCharacter);
    }
  }, [opened, selectedModel, selectedCharacter, models]);

  const selectedModelInfo = models.find(m => m.id === tempModel);
  const selectedCharacterInfo = tempCharacter && tempCharacter !== 'custom'
    ? characters.find(c => c.id === tempCharacter)
    : null;

  const handleClose = () => {
    // Reset temp selections to original values when closing
    setTempModel(selectedModel);
    setTempCharacter(selectedCharacter);
    onClose();
  };

  const handleConfirm = () => {
    onConfirm(tempModel, tempCharacter);
    onClose();
  };

  const handleCharacterSelect = (characterId: string | null) => {
    setTempCharacter(characterId);
    setCharacterModalOpen(false);
  };

  const handleModelSelect = (modelId: string) => {
    const model = models.find(m => m.id === modelId);
    // Prevent selection of paid models when not logged in
    if (!isLoggedIn && model && !model.free_tier) {
      return;
    }
    setTempModel(modelId);
    setModelModalOpen(false);
  };

  const handleCharacterModalModelSelect = (modelId: string) => {
    setTempModel(modelId);
    // Character modal will handle its own close after model recommendation
  };

  const getCharacterAvatar = () => {
    if (tempCharacter === null) {
      return <IconUser size={20} />;
    }
    if (tempCharacter === 'custom') {
      return '✏️';
    }
    if (selectedCharacterInfo) {
      return (
        <Avatar
          src={getCharacterImagePath(selectedCharacterInfo.id)}
          alt={selectedCharacterInfo.name}
          size={20}
        >
          <IconUser size={16} />
        </Avatar>
      );
    }
    return <IconUser size={20} />;
  };

  return (
    <>
      <Modal
        opened={opened}
        onClose={handleClose}
        title={
          <Group gap="xs">
            <IconMessagePlus size={20} />
            <Text size="lg" fw={600}>Configure New Chat</Text>
          </Group>
        }
        size="md"
        centered
      >
        <Stack gap="md">
          <Text size="sm" c="dimmed">
            Choose your AI model and character for the new conversation. You can change these settings or keep your current selections.
          </Text>

          {/* Character Selection */}
          <Card
            p="md"
            withBorder
            style={{ cursor: 'pointer' }}
            onClick={() => setCharacterModalOpen(true)}
          >
            <Group justify="space-between" align="center">
              <Group gap="md" align="center">
                {getCharacterAvatar()}
                <Box>
                  <Text size="sm" fw={500} c="dimmed">Character</Text>
                  <Text size="md" fw={600}>
                    {getCharacterDisplayText(tempCharacter, characters)}
                  </Text>
                  {selectedCharacterInfo && (
                    <Text size="xs" c="dimmed" lineClamp={1}>
                      {selectedCharacterInfo.description}
                    </Text>
                  )}
                </Box>
              </Group>
              <IconChevronRight size={16} color="gray" />
            </Group>
          </Card>

          <Divider />

          {/* Model Selection */}
          <Card
            p="md"
            withBorder
            style={{ cursor: 'pointer' }}
            onClick={() => setModelModalOpen(true)}
          >
            <Group justify="space-between" align="center">
              <Group gap="md" align="center">
                <IconBrain size={20} />
                <Box style={{ flex: 1, minWidth: 0 }}>
                  <Text size="sm" fw={500} c="dimmed">AI Model</Text>
                  <Text size="md" fw={600} truncate>
                    {selectedModelInfo ? selectedModelInfo.name : 'Select Model'}
                  </Text>
                  {selectedModelInfo && (
                    <>
                      <Group gap={4} mt={4}>
                        {getModelBadges(selectedModelInfo)}
                      </Group>
                      <Group gap="md" mt={8}>
                        <Group gap={4} align="center">
                          <IconCash size={12} color="gray" />
                          <Text size="xs" c="dimmed">
                            {formatPricing(selectedModelInfo)}
                          </Text>
                        </Group>
                        <Text size="xs" c="dimmed">
                          {selectedModelInfo.provider_count} provider{selectedModelInfo.provider_count !== 1 ? 's' : ''}
                        </Text>
                      </Group>
                    </>
                  )}
                </Box>
              </Group>
              <IconChevronRight size={16} color="gray" />
            </Group>
          </Card>

          <Group justify="flex-end" mt="md">
            <Button variant="light" onClick={handleClose}>
              Cancel
            </Button>
            <Button onClick={handleConfirm}>
              Continue with selection
            </Button>
          </Group>
        </Stack>
      </Modal>

      <CharacterSelectionModal
        opened={characterModalOpen}
        onClose={() => setCharacterModalOpen(false)}
        selectedCharacter={tempCharacter}
        onCharacterSelect={handleCharacterSelect}
        onModelSelect={handleCharacterModalModelSelect}
        models={models}
        characters={characters}
      />

      <ModelSelectionModal
        opened={modelModalOpen}
        onClose={() => setModelModalOpen(false)}
        models={models}
        selectedModel={tempModel}
        onModelSelect={handleModelSelect}
        isLoggedIn={isLoggedIn}
      />
    </>
  );
});

NewChatModal.displayName = 'NewChatModal';